import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, IconButton, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { resolvePageBack } from '../utils/pageBack';

/**
 * Єдина кнопка повернення для вкладених сторінок (PWA без browser back).
 *
 * @param {object} props
 * @param {string} [props.to] — явний шлях (має пріоритет над картою)
 * @param {string} [props.label] — підпис кнопки
 * @param {() => void} [props.onBack] — замість navigate
 * @param {React.ReactNode} [props.title] — заголовок справа від кнопки
 * @param {'page'|'dialog'} [props.variant]
 * @param {object} [props.sx]
 */
export default function PageBackHeader({
  to,
  label,
  onBack,
  title,
  variant = 'page',
  sx,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const resolved = resolvePageBack(location.pathname, location.search, location.state);
  const target = to || resolved.to;
  const btnLabel = label || resolved.label || 'Назад';

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    if (target) navigate(target);
    else navigate('/');
  };

  if (variant === 'dialog') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          width: '100%',
          ...sx,
        }}
      >
        <Typography variant="h6" component="div" sx={{ flex: 1, pr: 1 }}>
          {title}
        </Typography>
        <IconButton edge="end" onClick={handleBack} aria-label="Закрити" size="small">
          <CloseIcon />
        </IconButton>
      </Box>
    );
  }

  // Якщо немає куди йти і немає onBack — не показуємо
  if (!onBack && !target && !resolved.show) return null;

  return (
    <Box sx={{ mb: title ? 1.5 : 1, ...sx }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        size="small"
        sx={{ ml: -1, mb: title ? 0.5 : 0 }}
      >
        {btnLabel}
      </Button>
      {title ? (
        <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          {title}
        </Typography>
      ) : null}
    </Box>
  );
}

/** Компактна кнопка для AppBar Layout. */
export function AppBarBackButton({ sx }) {
  const navigate = useNavigate();
  const location = useLocation();
  const resolved = resolvePageBack(location.pathname, location.search, location.state);
  if (!resolved.show || !resolved.to) return null;

  return (
    <Button
      color="inherit"
      startIcon={<ArrowBackIcon />}
      onClick={() => navigate(resolved.to)}
      size="small"
      sx={{
        mr: 1,
        textTransform: 'none',
        minWidth: 0,
        px: { xs: 0.75, sm: 1.5 },
        ...sx,
      }}
      aria-label={resolved.label || 'Назад'}
    >
      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
        {resolved.label || 'Назад'}
      </Box>
      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
        Назад
      </Box>
    </Button>
  );
}
